import React, {useRef, useState} from 'react'
import {IBaseProps} from '@type/page'
import {
    autoUpdate,
    flip,
    offset,
    safePolygon,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useHover,
    useInteractions,
    useRole,
    arrow,
} from '@floating-ui/react'

import {Placement} from '@floating-ui/utils'

interface IProps extends IBaseProps, React.PropsWithChildren {
    baseChildren?: React.ReactNode
    floatingStyle?: React.CSSProperties
    placement?: Placement
    enabled?: boolean
    enableHover?: boolean
    disappearAfterClick?: boolean
}

const GAP = 3

const WizCustomViewMenu: React.FC<IProps> = ({
    className,
    children,
    baseChildren,
    floatingStyle,
    placement,
    enabled = true,
    enableHover = false,
    disappearAfterClick = false,
}) => {
    const [isOpen, setIsOpen] = useState(false)

    const {x, y, strategy, refs, context} = useFloating({
        open: isOpen,
        placement: placement || 'bottom-start',
        onOpenChange: setIsOpen,
        middleware: [offset(GAP)],
        whileElementsMounted: autoUpdate,
    })

    const click = useClick(context, {enabled: !enableHover})
    const hover = useHover(context, {
        enabled: enableHover,
        handleClose: safePolygon({
            requireIntent: false,
        }),
    })
    const dismiss = useDismiss(context)
    const role = useRole(context)
    const {getReferenceProps, getFloatingProps} = useInteractions([hover, click, dismiss, role])

    const onClose = (e?: React.MouseEvent<HTMLDivElement>) => {
        e?.preventDefault()
        e?.stopPropagation()
        disappearAfterClick && setIsOpen(false)
    }

    return (
        <div className={`${className}`}>
            <div
                ref={refs?.setReference}
                {...getReferenceProps({
                    onClick: e => {
                        e.preventDefault()
                        e.stopPropagation()
                    },
                })}>
                {baseChildren}
            </div>
            {enabled && isOpen && (
                <div
                    ref={refs?.setFloating}
                    style={{
                        position: strategy,
                        top: y ?? 0,
                        left: x ?? 0,
                        width: 'max-content',
                        ...floatingStyle,
                    }}
                    className={'z-tooltip'}
                    onClick={onClose}
                    {...getFloatingProps()}>
                    {children}
                </div>
            )}
        </div>
    )
}

export default WizCustomViewMenu
